import musicData from '~/data/musicData';
import musicTiming from '~/data/musicTiming';
import { PATTERNS, type PatternParts } from '~/types/pattern';
import type { DevicePattern } from '~/types';

// Pool of default hardcoded patterns
const DEFAULT_PATTERNS_POOL = [
  // Default: FM Synth pattern (G# Minor Pentatonic, AABAAC structure, quarter notes)
  {
    structure: PATTERNS.find(p => p.name === "AABAAC") || PATTERNS[0],
    rootNote: musicData.rootNotes.find(n => n.note === "G♯") || musicData.rootNotes[0],
    scale: musicData.scales.find(s => s.id === "pentatonic-min") || musicData.scales[0],
    noteLength: musicTiming.noteLengths[2], // 4n (quarter notes)
    space: 35,
    dissonance: 0,
    spread: 12,
    range: 25,
    parts: {
      A: [
        { note: null, duration: "4n" },
        { note: null, duration: "4n" },
        { note: null, duration: "4n" },
        { note: "B3", duration: "4n" },
      ],
      B: [
        { note: "F♯3", duration: "4n" },
        { note: "F♯2", duration: "4n" },
        { note: "C♯3", duration: "4n" },
        { note: "D♯2", duration: "4n" },
      ],
      C: [
        { note: "G♯4", duration: "4n" },
        { note: "D♯3", duration: "4n" },
        { note: "F♯3", duration: "4n" },
        { note: "F♯3", duration: "4n" },
      ],
    } as PatternParts,
  },
  // Backup: Original Device 1 pattern (G Major, AABB structure, eighth notes)
  {
    structure: PATTERNS.find(p => p.name === "AABB") || PATTERNS[0],
    rootNote: musicData.rootNotes.find(n => n.note === "G") || musicData.rootNotes[0],
    scale: musicData.scales.find(s => s.id === "ionian-maj") || musicData.scales[0],
    noteLength: musicTiming.noteLengths[3],
    space: 35,
    dissonance: 5,
    spread: 7,
    range: 12,
    parts: {
      A: [
        { note: "B2", duration: "8n" },
        { note: "B2", duration: "8n" },
        { note: "B2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: "C2", duration: "8n" },
        { note: "C2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: "G2", duration: "8n" },
      ],
      B: [
        { note: null, duration: "8n" },
        { note: "B2", duration: "8n" },
        { note: "B2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: "E2", duration: "8n" },
      ],
    } as PatternParts,
  },
  {
    structure: PATTERNS.find(p => p.name === "ABCB") || PATTERNS[1],
    rootNote: musicData.rootNotes.find(n => n.note === "D♯") || musicData.rootNotes[0],
    scale: musicData.scales.find(s => s.id === "ionian-min") || musicData.scales[0],
    noteLength: musicTiming.noteLengths[3],
    space: 35,
    dissonance: 5,
    spread: 7,
    range: 12,
    parts: {
      A: [
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: null, duration: "8n" },
      ],
      B: [
        { note: "F2", duration: "8n" },
        { note: "F2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: "G2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
      ],
      C: [
        { note: "G#2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: "A#2", duration: "8n" },
        { note: "A#2", duration: "8n" },
        { note: "A#2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: "A#2", duration: "8n" },
        { note: null, duration: "8n" },
      ],
    } as PatternParts,
  },
  {
    structure: PATTERNS.find(p => p.name === "ABCB") || PATTERNS[1],
    rootNote: musicData.rootNotes.find(n => n.note === "G♯") || musicData.rootNotes[0],
    scale: musicData.scales.find(s => s.id === "pentatonic-maj") || musicData.scales[0],
    noteLength: musicTiming.noteLengths[3],
    space: 35,
    dissonance: 5,
    spread: 7,
    range: 12,
    parts: {
      A: [
        { note: "G#2", duration: "8n" },
        { note: "G#2", duration: "8n" },
        { note: "G#2", duration: "8n" },
        { note: "G#2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: "A#2", duration: "8n" },
        { note: "A#2", duration: "8n" },
      ],
      B: [
        { note: null, duration: "8n" },
        { note: "C2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: "D#2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
      ],
      C: [
        { note: "F2", duration: "8n" },
        { note: "F2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
        { note: "G#2", duration: "8n" },
        { note: null, duration: "8n" },
        { note: null, duration: "8n" },
      ],
    } as PatternParts,
  },
];

/**
 * Generate a default pattern configuration for a device
 * @param patternIndex - Optional index to select a specific pattern (default: random)
 */
export function getDefaultDevicePattern(patternIndex?: number): DevicePattern {
  const randomIndex = patternIndex ?? Math.floor(Math.random() * DEFAULT_PATTERNS_POOL.length);
  const selectedDefault = DEFAULT_PATTERNS_POOL[randomIndex];

  return {
    rootNote: { ...selectedDefault.rootNote },
    scale: { ...selectedDefault.scale },
    scaleMode: selectedDefault.scale.mode,
    pattern: { ...selectedDefault.structure },
    // Deep copy patternParts to avoid shared references between devices
    patternParts: JSON.parse(JSON.stringify(selectedDefault.parts)) as PatternParts,
    beatsPerBar: 4, // 4/4 time signature (may be 3 for triplets later)
    tempo: 110, // Default BPM
    swing: 0, // Default swing amount (-100 to +100, 0 = no swing)
    space: selectedDefault.space,
    spaceMin: 20,
    spaceMax: 50,
    dissonance: selectedDefault.dissonance,
    dissonanceMin: 0,
    dissonanceMax: 2,
    spread: selectedDefault.spread,
    spreadMin: 0,
    spreadMax: 24,
    range: selectedDefault.range,
    rangeMin: 12,
    rangeMax: 37,
    tonicGravity: 0,
    tonicGravityMin: -6,
    tonicGravityMax: 6,
    scrambleToggles: {
      scrambleStructure: true,
      scrambleRootNote: true,
      scrambleMode: true,
      scrambleScale: true,
      scrambleSpace: true,
      scrambleDissonance: true,
      scrambleSpread: true,
      scrambleRange: true,
      scrambleTonicGravity: true,
    },
  };
}

/**
 * Create a deep copy of a pattern (for copying between devices)
 */
export function copyDevicePattern(pattern: DevicePattern): DevicePattern {
  return JSON.parse(JSON.stringify(pattern));
}
